import {
  Box,
  Button,
  Card,
  List,
  MenuItem,
  Popper,
  Theme,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAvailableSites } from 'src/hooks/api/use-available-sites';
import { useAvailableTenants } from 'src/hooks/api/use-available-tenants';
import { usePopover } from 'src/hooks/use-popover';
import { useViewContextName } from 'src/hooks/use-view-context-name';
import { Briefcase as BriefcaseIcon } from 'src/icons/briefcase';
import { ChevronDown } from 'src/icons/chevron-down';
import { useUserStore } from 'src/store/user-store';
import { createOutsideClickHandler } from 'src/utils/create-outside-click-handler';
import { AvailableSitesPopper } from '../molecules/available-sites-popper';
import { AvailableTenantsPopper } from '../molecules/available-tenants-popper';

export const PanelContextSelector = () => {
  const { t } = useTranslation();

  const { viewContext, directoryToken } = useUserStore();

  const { data: availableTenants } = useAvailableTenants();

  const selectedTenant = availableTenants?.resources.find(
    tenant => tenant.token === directoryToken,
  );

  const { data: availableSites } = useAvailableSites();

  const selectedSite = availableSites?.resources.find(
    site => site.token === directoryToken,
  );

  const getViewContextName = useViewContextName(
    viewContext,
    selectedSite,
    selectedTenant,
  );

  const {
    palette: { mode },
  } = useTheme();

  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('md'),
  );

  const [
    mainMenuAnchorRef,
    openMainMenu,
    handleOpenMainMenu,
    handleCloseMainMenu,
  ] = usePopover();

  const [
    tenantMenuAnchorRef,
    openTenantMenu,
    handleOpenTenantMenu,
    handleCloseTenantMenu,
  ] = usePopover<HTMLDivElement>();

  const [
    siteMenuAnchorRef,
    openSiteMenu,
    handleOpenSiteMenu,
    handleCloseSiteMenu,
  ] = usePopover<HTMLLIElement>();

  const handleCloseAllMenus = () => {
    handleCloseMainMenu();
    handleCloseTenantMenu();
    handleCloseSiteMenu();
  };

  useEffect(() => {
    const excludedRefs = [
      mainMenuAnchorRef,
      tenantMenuAnchorRef,
      siteMenuAnchorRef,
    ];

    createOutsideClickHandler(excludedRefs, handleCloseAllMenus);
  }, []);

  return (
    <>
      <Button
        color="primary"
        onClick={openMainMenu ? handleCloseMainMenu : handleOpenMainMenu}
        ref={mainMenuAnchorRef}
        size={isMobile ? 'medium' : 'large'}
        variant="text"
        startIcon={
          <BriefcaseIcon
            fontSize="small"
            sx={{
              color: mode === 'dark' ? '#fff' : '#000',
              width: '16px',
            }}
          />
        }
        endIcon={<ChevronDown fontSize="small" />}
        sx={{
          ml: 1,
          mr: 2,
          alignItems: 'center',
          fontWeight: 400,
          color: 'text.primary',
          '&:hover': {
            color: 'text.primary',
          },
          '&:active': {
            color: 'text.primary',
          },
          '&:focus': {
            color: 'text.primary',
          },
        }}>
        {getViewContextName()}
      </Button>

      <Popper
        ref={tenantMenuAnchorRef}
        anchorEl={mainMenuAnchorRef.current}
        open={openMainMenu}
        placement="bottom-end"
        onMouseLeave={() => {
          handleCloseTenantMenu();
          handleCloseSiteMenu();
        }}
        sx={{ margin: '10px 0 !important' }}>
        <Card variant="outlined">
          <List>
            <MenuItem
              onMouseEnter={() => {
                handleOpenTenantMenu();
                handleCloseSiteMenu();
              }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                }}>
                {t('common:viewContextSelector.tenant')}

                <ChevronDown
                  fontSize="small"
                  sx={{ ml: 2, transform: 'rotate(-90deg)' }}
                />
              </Box>
            </MenuItem>

            <MenuItem
              ref={siteMenuAnchorRef}
              onMouseEnter={() => {
                handleOpenSiteMenu();
                handleCloseTenantMenu();
              }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                }}>
                {t('common:viewContextSelector.site')}

                <ChevronDown
                  fontSize="small"
                  sx={{ ml: 2, transform: 'rotate(-90deg)' }}
                />
              </Box>
            </MenuItem>
          </List>
        </Card>
      </Popper>

      <AvailableTenantsPopper
        anchorRef={tenantMenuAnchorRef}
        open={openTenantMenu}
        onClose={handleCloseAllMenus}
        onMouseLeave={handleCloseTenantMenu}
        onMouseEnter={handleOpenTenantMenu}
        placement="right-start"
      />

      <AvailableSitesPopper
        anchorRef={siteMenuAnchorRef}
        open={openSiteMenu}
        onClose={handleCloseAllMenus}
        onMouseLeave={handleCloseSiteMenu}
        onMouseEnter={handleOpenSiteMenu}
        placement="right-start"
      />
    </>
  );
};
